import axios, {
  type AxiosInstance,
  type AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios';
import { tokenManager } from '@/lib/auth/token-manager';
import { cognitoAuthService } from '@/lib/auth/cognito.service';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
const API_TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT) || 30000;

// Token refresh state management
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (error: unknown) => void;
}> = [];

/**
 * Process queued requests after token refresh completes.
 */
function processQueue(error: unknown, token: string | null = null): void {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
}

/**
 * Attempt to refresh the access token using the refresh token.
 * Returns the new access token or throws if refresh fails.
 */
async function refreshAccessToken(): Promise<string> {
  await cognitoAuthService.refreshTokens();
  const newToken = localStorage.getItem('coi-live-token');
  if (!newToken) {
    throw new Error('Token refresh succeeded but no token in storage');
  }
  return newToken;
}

/**
 * Generate a unique correlation ID for request tracing.
 */
function generateCorrelationId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Structured logger for API requests.
 * In production, this would send to CloudWatch or similar.
 */
const apiLogger = {
  request: (correlationId: string, method: string, url: string) => {
    console.log(`[API] ${correlationId} → ${method} ${url}`);
  },
  response: (
    correlationId: string,
    status: number,
    duration: number
  ) => {
    console.log(`[API] ${correlationId} ← ${status} (${duration}ms)`);
  },
  error: (
    correlationId: string,
    status: number | undefined,
    message: string
  ) => {
    console.error(`[API] ${correlationId} ✕ ${status ?? 'NETWORK'}: ${message}`);
  },
};

// Extend axios config to include our custom properties
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    correlationId: string;
    startTime: number;
  };
}

/**
 * Axios instance with interceptors for:
 * - Authentication token injection
 * - Request/response logging with correlation IDs
 * - Error handling
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - handles token refresh before requests
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig): Promise<CustomAxiosRequestConfig> => {
    const correlationId = generateCorrelationId();
    const customConfig = config as CustomAxiosRequestConfig;

    // Attach metadata for logging
    customConfig.metadata = {
      correlationId,
      startTime: Date.now(),
    };

    // Add correlation ID to headers for backend tracing
    customConfig.headers.set('X-Correlation-ID', correlationId);

    // Check if token needs refresh BEFORE making request
    if (tokenManager.isExpired()) {
      const refreshToken = tokenManager.getRefreshToken();
      if (refreshToken && !isRefreshing) {
        try {
          isRefreshing = true;
          await refreshAccessToken();
          console.log('[API] Token refreshed before request');
        } catch (error) {
          console.warn('[API] Token refresh failed, request may fail with 401');
        } finally {
          isRefreshing = false;
        }
      }
    }

    // Get (potentially refreshed) token from localStorage
    const token = localStorage.getItem('coi-live-token');
    if (token) {
      customConfig.headers.set('Authorization', `Bearer ${token}`);
    }

    // Log request
    apiLogger.request(correlationId, config.method?.toUpperCase() ?? 'GET', config.url ?? '');

    return customConfig;
  },
  (error) => Promise.reject(error)
);

// Extend config to track retry attempts
interface RetryableAxiosRequestConfig extends CustomAxiosRequestConfig {
  _retry?: boolean;
}

// Response interceptor with automatic token refresh on 401
apiClient.interceptors.response.use(
  (response) => {
    const config = response.config as CustomAxiosRequestConfig;
    const { correlationId, startTime } = config.metadata ?? {
      correlationId: 'unknown',
      startTime: Date.now(),
    };
    const duration = Date.now() - startTime;

    apiLogger.response(correlationId, response.status, duration);

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableAxiosRequestConfig | undefined;
    const { correlationId } = originalRequest?.metadata ?? { correlationId: 'unknown' };

    apiLogger.error(correlationId, error.response?.status, error.message);

    // Handle 401 - attempt token refresh and retry
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (token && originalRequest.headers) {
              originalRequest.headers.set('Authorization', `Bearer ${token}`);
            }
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        processQueue(null, newToken);

        // Retry original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
        }
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        // Clear all auth data
        localStorage.removeItem('coi-live-token');
        localStorage.removeItem('coi-live-auth-tokens');

        // Dispatch event for auth store
        window.dispatchEvent(new CustomEvent('auth:expired'));

        // Redirect to login with session expired message
        if (window.location.pathname !== '/login') {
          window.location.href = '/login?session=expired';
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export { apiLogger, generateCorrelationId };
